'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { useGetOsName } from '@hooks/useGetOsName';
import { useIsMobileDevice } from '@hooks/useIsMobileDevice';
import { useIsUnsupportedInAppBrowser } from '@hooks/useIsUnsupportedInAppBrowser';
import { AppDownloadUrlSourceType, getAppDownloadUrl } from '@domain/routes/getAppDownloadUrl';
import { getAppUrlWithPath } from '@domain/routes/urls';
import { AppRatingSection } from '../AppRatingSection/AppRatingSection';
import { Button } from '../Clickables/Buttons/Button';
import { ExternalLink } from '../Clickables/ExternalLink/ExternalLink';
import { Heading } from '../Heading/Heading';
import { Modal } from '../Modal/Modal';
import phone from './assets/phone.png';

export const AppRedirectModal = () => {
  const pathname = usePathname();
  const queryParams = useSearchParams();
  const clientHasRendered = useClientHasRendered();
  const isMobileDevice = useIsMobileDevice();

  const isAppShare = clientHasRendered && queryParams.get('appshare') === 'true';
  const appUrlPath = queryParams.get('appurl') ?? pathname;
  const [isDismissed, setIsDismissed] = useState(false);
  const isModalOpen = clientHasRendered && isAppShare && isMobileDevice && !isDismissed;
  const isUserComingFromUnsupportedApp = useIsUnsupportedInAppBrowser();
  const { currentPage } = useAnalytics();

  const osName = useGetOsName();
  const appDownloadUrl = getAppDownloadUrl({
    appDownloadSourceType: AppDownloadUrlSourceType.AppRedirectModal,
    currentPageId: currentPage,
    osName,
  });

  const appUrl = appUrlPath ? getAppUrlWithPath(appUrlPath) : undefined;

  useEffect(() => {
    if (isUserComingFromUnsupportedApp && appUrl) {
      window.location.href = appUrl;
    }
  }, [appUrl, isUserComingFromUnsupportedApp]);

  if (!isAppShare) {
    return <></>;
  }

  const downloadAppClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'AppRedirectModal',
        type: 'download',
        page: currentPage,
      },
    });
  };

  const continueWebClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'AppRedirectModal',
        type: 'read-more',
        page: currentPage,
      },
    });
    setIsDismissed(true);
  };

  return isModalOpen ? (
    <Modal
      size="full"
      outerClassName="z-modal-consent w-full"
      className="w-full"
      isOpen
      disableClose
    >
      <div className="flex h-dvh max-h-dvh flex-col items-center justify-start gap-4 sm:justify-start">
        <div className="bg-tuna-tint relative flex h-2/5 w-full items-end justify-center">
          <Image
            placeholder="blur"
            alt="Fishbrain app"
            className="w-full object-contain px-16 pt-6"
            src={phone}
            fill
            sizes="100vw"
          />
        </div>
        <div className="flex w-full flex-col items-center justify-center gap-6 px-12">
          <Heading variant="xl" className="text-center">
            Fishbrain is better in the app
          </Heading>
          <AppRatingSection className="items-center justify-center gap-6 sm:flex-row sm:justify-start" />

          <div className="flex w-full flex-col items-center justify-center gap-3 pb-6">
            <ExternalLink
              variant="buttonPrimary"
              href={appDownloadUrl}
              size="default"
              className="w-full"
              onClick={downloadAppClick}
            >
              Download
            </ExternalLink>
            <Button
              variant="buttonSecondary"
              size="default"
              className="w-full"
              onClick={continueWebClick}
            >
              Continue on web
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  ) : (
    <></>
  );
};
